<template>
    <div class="academic-monitoring-schedule-creation">
        <div class="d-flex mb-3 flex-wrap" v-if="monitors.length > 1">
            <div v-for="(tab, i) in monitors" @click="changeTab(i)"
                class="flex-shrink-1 monitor-tab my-1"
                :class="{'monitor-tab-active': i == activeTab}"
                :key="`monitor-`+i"
                :data-test="`create-intervention-monitoring-tab-${i}`"
            >
                <v-icon color="#495B8F" class="mr-2" v-if="submitted && !monitorFormValid[i]">fas fa-exclamation-circle</v-icon>
                <i v-if="tab && tab.sub_category_id && tab.sub_category_id.id && !monitorLoaded[i]" class="fas fa-spinner fa-spin"></i>
                {{ getNumber(i) }} Measure
            </div>
        </div>
        <div v-for="(monitor, i) in monitors" :key="`monitor-cfg-${i}`">
            <v-form v-model="monitorFormValid[i]" v-show="activeTab == i" ref="monitorConfig">
                <div>
                    <fe-label style="display:flex; align-items:center;">Monitoring User
                        <fe-tooltip class="ml-2" tooltip="The monitoring user observes student progress on the selected measure. Only the monitoring user and primary interventionist can enter scores.">
                            <v-icon color="#495B8F">fal fa-info-circle</v-icon>
                        </fe-tooltip>
                    </fe-label>
                    <fe-remote-combo
                        ref="monitorUser"
                        data-test="create-intervention-monitoring-user"
                        :items="monitoringUsers"
                        itemText="user_full_name"
                        itemValue="id"
                        v-model="monitor.monitor_user_id"
                        @input="monitorUserTouched[i] = true"
                        byId
                        :rules="$fieldValidators('select', 'Monitor user', { required: true })"
                    />
                </div>

                <div>
                    <fe-label>Measure Type</fe-label>
                    <fe-remote-combo
                        data-test="create-intervention-monitoring-measure-type"
                        :items="measureTypeItemsFiltered"
                        itemText="display_name"
                        itemValue="id"
                        :clearable="!forceMonitoringTypeId"
                        :readonly="!!forceMonitoringTypeId"
                        v-model="monitor.sub_category_id"
                        @change="changeMeasureType(i)"
                        :rules="$fieldValidators('select', 'Monitor', { required: true })"
                    >
                        <template v-slot:item="data">
                            <v-list-item-content>
                                <v-list-item-title v-safe-html="data.item.display_name" />
                                <v-list-item-subtitle>
                                        {{data.item.data_point_type_name}}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                        <template v-slot:message="{ message }">
                            <v-icon color="#BF0D00" class="mr-2">fal fa-exclamation-circle</v-icon>
                            <span style="color: #BF0D00">Select a monitoring type.</span>
                        </template>
                    </fe-remote-combo>
                </div>

                <banner v-if="upperBanner.show" class="mb-4" :usage="upperBanner.usage" :text="upperBanner.text"></banner>

                <div v-if="monitors.length > 1" style="margin-left: -10px" class="d-flex">
                    <v-checkbox
                        data-test="create-intervention-monitoring-primary"
                        label="Set as the primary measure"
                        v-model="monitor.primary_flag"
                        @change="setPrimary(monitor, i)"
                    />
                    <fe-tooltip tooltip="If you make this the primary measure, this measure will be used for all Rate of Improvement (ROI) charts and reports."><v-icon style="margin-top: 2px">fal fa-info-circle</v-icon></fe-tooltip>
                </div>

                <template v-if="measuredMonitorSelected(i)">
                    <fe-spinner v-if="!monitorLoaded[i]"/>
                    <template v-else>
                        <banner v-if="!showGoalErrors[i] && monitorBanner[i]" class="mb-4" :usage="monitorBanner[i].usage" :text="monitorBanner[i].text"></banner>
                        <v-input ref="goalError" v-show="showGoalErrors[i]" v-model="monitors[i].students" :rules="[v => validateGoals(v, i)]">
                            <template v-slot:message="{ message }">
                                <banner class="mb-4" usage="warning" :text="message" />
                            </template>
                        </v-input>

                        <div class="mb-5">
                            <fe-grid
                                v-if="columnHeaders[monitor.sub_category_id.id]"
                                class="academic-monitor-schedule-student-goal-grid mb-5"
                                data-test="create-intervention-monitoring-student-goals"
                                ref="monitorGrid"
                                v-model="monitors[i].students"
                                :showToolbar="false"
                                :columnDefs="columnHeaders[monitor.sub_category_id.id]"
                                :rowData="monitors[i].students"
                                stopEditingWhenCellsLoseFocus
                                @cellValueChanged="updateStudentGoalValue(i, $event)"
                                :gridOptionOverrides="gridOptions"
                            />
                        </div>

                        <div class="d-flex">
                            <fe-date-picker
                                ref="startDatePicker"
                                data-test="create-intervention-monitoring-start-date"
                                label="Start Date"
                                :rules="$fieldValidators('text', 'Start date', { required: true })"
                                v-model="monitor.start_date"
                                class="mr-2 flex-grow-1"
                                dateFormat="M/D/YYYY"
                                prependIcon="far fa-calendar-day"
                                @input="editField('startDatePicker', i)"
                            />
                            <fe-date-picker
                                ref="endDatePicker"
                                data-test="create-intervention-monitoring-end-date"
                                label="End Date"
                                :rules="[v => !monitors[i].start_date ? true : (!$dayjs(v).isBefore(monitors[i].start_date) || 'Not a valid end date')].concat($fieldValidators('text', 'End date', { required: true }))"
                                v-model="monitor.end_date"
                                class="flex-grow-1"
                                dateFormat="M/D/YYYY"
                                prependIcon="far fa-calendar-day"
                                @input="editField('endDatePicker', i)"
                            />
                        </div>

                        <div>
                            <fe-label>Every Number of Weeks</fe-label>
                            <fe-remote-combo
                                data-test="create-intervention-monitoring-number-of-weeks"
                                :items="weekOptions"
                                v-model="monitor.monitor_frequency_cnt"
                                :rules="$fieldValidators('number', 'Number of weeks', { required: true })"
                                byId
                            >
                                <template v-slot:message="{ message }">
                                    <v-icon color="#BF0D00" class="mr-2">fal fa-exclamation-circle</v-icon>
                                    <span style="color: #BF0D00">Enter a valid number of weeks.</span>
                                </template>
                            </fe-remote-combo>
                        </div>

                        <div>
                            <weekday-picker :required="true" data-test="create-intervention-monitoring-weekday-picker" v-model="monitor.monitor_week_days" threeLetter secondary/>
                        </div>

                        <div class="d-flex mt-2">
                            <div class="pr-2 flex-grow-1">
                                <fe-label>Start Time</fe-label>
                                <fe-time-scroll-picker
                                    ref="startTimePicker"
                                    data-test="create-intervention-monitoring-start-time"
                                    v-model="monitor.start_time"
                                    @input="(v) => {editField('startTimePicker', i); setEndTime(v, monitor, i)}"
                                />
                            </div>
                            <div class="flex-grow-1">
                                <fe-label>End Time</fe-label>
                                <fe-time-scroll-picker
                                    ref="endTimePicker"
                                    data-test="create-intervention-monitoring-end-time"
                                    v-model="monitor.end_time"
                                    :rules="[v => !monitors[i].start_time ? true : (v && !$dayjs('2000-01-01 ' + v).isBefore('2000-01-01 ' + monitors[i].start_time) || 'Not a valid end time')]"
                                    @input="editField('endTimePicker', i)"
                                />
                            </div>
                        </div>
                    </template>
                </template>
            </v-form>
        </div>

        <div v-if="monitors.length > 1" style="text-align: right; margin-right: -8px;">
            <fe-btn @click="removeMonitor(activeTab)" usage="warning" data-test="create-intervention-monitoring-remove-measure"><v-icon class="mr-2">fal fa-trash</v-icon>Remove {{ getNumber(activeTab) }} Measure</fe-btn>
        </div>

        <div class="d-flex card-bottom">
            <fe-btn usage="secondary" @click="$emit('go','back')" data-test="create-intervention-monitoring-back">Back</fe-btn>
            <fe-btn @click="nextStep" :disabled="loading > 0" data-test="create-intervention-monitoring-step-add">Continue</fe-btn>
            <div class="flex-fill justify-end">
                <fe-btn usage="tertiary" v-if="showAddAnotherMeasure" @click="addMonitor" data-test="create-intervention-monitoring-add-measure">Add Another Measure</fe-btn>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import {mapState} from 'vuex'
import WeekdayPicker from '@/components/common/button/WeekdayPicker'
import StudentMonitorList from '../../StudentMonitorList'
import timeMixin from '../mixins/time.js'
import { decimalString } from '@/util/decimal.js'
import Banner from '@/components/common/Banner'
import IconHeader from '../renderers/iconHeader.vue'

export default {
    name: 'MonitorAssessment',

    components: {
        WeekdayPicker,
        StudentMonitorList,
        Banner,
        iconHeader: IconHeader,
    },

    mixins: [timeMixin],

    props: {
        value: {
            type: Array,
            default() { return [] }
        },
        students: {
            type: Array,
            default() { return [] }
        },
        forceMonitoringTypeId: {
            type: Number,
            required: false,
        },
        schedule: {
            type: Object,
            default: () => {}
        },
        monitoringUsers: {
            type: Array,
            default: [],
        },
        fastbridgeMonitor: {
            type: Object,
            required: false,
            default: () => {}
        },
    },

    data() {
        return {
            grades: [],
            alphaMaps: {},
            monitors: [],
            monitorFormValid: [],
            // true once the user has edited the Monitoring User field of that monitor
            monitorUserTouched: [],
            monitorLoaded: [],
            monitorBanner: [],
            submitted: false,
            loading: 0,
            activeTab: 0,
            columnHeaders: {},
            studentMonitorDialog: {
                show: false,
                params: null,
                records: [],
                monitor: null
            },
            gridOptions: {
                context: { componentParent: this },
                suppressRowTransform: true,
                getRowHeight: (params) => {
                    return params.data.rowSpan > 1 ? 46 : 46
                }
            },
            upperBanner: [{
                show: false,
                text: null,
                usage: 'info'
            }],
            measureTypeItems: [],
        }
    },

    computed: {
        ...mapState('global', ['sessionUser', 'currentYear', 'userPreferences']),
        ...mapState('intervention', ['categoryId']),
        monitorRules() {
            return [
                v => {
                    if (v.some(s => s.originalGoal === '' && s.goal === '')) {
                        return 'Each student must have a goal.'
                    }
                    return true
                }
            ]
        },
        // Create a URL specific to the selected students, category, and monitor sub_category_id
        monitoringTypeURL() {
            const stuIDs = this.students.map(stu => stu.student_id).join(',')
            return this.monitors.map(monitor => this.$models.getUrl('academicMonitor', 'read') + `&school_date=${(monitor.start_date+'').replace(/^(\d+)\/(\d+)\/(\d+)$/, '$3-$1-$2')}&school_student_id=${stuIDs}&category_id=${this.categoryId}&sub_category_id=${monitor.sub_category_id?.id || ''}`)
        },
        showGoalErrors() {
            return this.submitted ? this.monitors.map(m => this.validateGoals(m.students) !== true) : []
        },
        getSchoolId() {
            // school_id from add measure popup || school_id new intervention creation process
            return this.students[0]?.school_id || this.$store.state.intervention.school.id
        },
        weekOptions() {
            let options = []
            for(let i = 1; i < 53; i++) {
                options.push({ id: i, name: i })
            }
            return options
        },
        showAddAnotherMeasure() {
            return this.monitors.length > 0
                && this.monitors[0].sub_category_id
                && this.monitors[0].sub_category_id.id != 'not_measured'
        },
        measureTypeItemsFiltered() {
            if (this.monitors.length <= 1) { // if there's only one monitor, add the not_measured option back in
                return [{
                    id: 'not_measured',
                    display_name: 'This intervention is not monitored by an assessment'
                }, ...this.measureTypeItems]
            }
            return this.measureTypeItems
        },
        topSecretModeActive(){
            return this.$isTopSecretModeActive(this.sessionUser, this.userPreferences)
        },
    },

    watch: {
        monitors: {
            deep: true,
            handler(newValue, oldValue) {
                this.$emit('input', newValue)
            }
        },
    },

    async created() {
        this.monitors = this.$_.cloneDeep(this.value)
        if (!this.monitors.length) {
            await this.addMonitor()
        }

        // load grades
        this.loading++
        this.$modelGet('grade').then(gradeResponse => {
            this.grades = gradeResponse.map(grade => {
                // want to return only numeric value if ordinal (e.g. return 3 for 3rd but keep EC and P4)
                grade.name = grade.name.replace(/^(\d+)(st|rd|th|nd)$/gi, '$1')
                return grade
            })

            // add any previously selected grades the user might not have access to
            this.monitors.forEach((monitor, index) => {
                monitor.students.forEach(student => {
                    if (!student.assessment_grade_id) {
                        return
                    }
                    const grade = this.grades.find(grade => grade.id == student.assessment_grade_id)
                    if (!grade) {
                        this.grades.push({
                            id: student.assessment_grade_id,
                            name: student.assessment_grade_desc,
                            rank: student.assessment_grade_rank,
                        })
                    }
                })
                this.$set(this.upperBanner, index, {
                    show: false,
                    text: null,
                    usage: 'info'
                })
                this.loadMonitorDependencies(index)
            })
            this.resetValidation()
        }).finally(() => this.loading--)

        this.loading++
        this.$axios.get(this.monitoringTypeURL[this.activeTab])
        .then(response => this.measureTypeItems = response.data.subcategories)
        .finally(() => this.loading--)
    },

    methods: {
        async addMonitor() {
            const primary_flag = this.monitors.length === 0
            let subcategory = null
            let students = []

            if (this.forceMonitoringTypeId) {
                let rec = await this.$axios.get(`subcategories.php?action=get&id=${this.forceMonitoringTypeId}`).then(res => res.data);
                subcategory = rec.subcategories[0]
            }

            // If FastBridge PM...
            if (this.monitors.length === 0 && this.fastbridgeMonitor?.sub_category_id) {
                // get subcategory (monitor type) using supplied id
                if (this.fastbridgeMonitor?.sub_category_id) {
                    let rec = await this.$axios.get(`subcategories.php?action=get&id=${this.fastbridgeMonitor?.sub_category_id}`).then(res => res.data);
                    subcategory = rec.subcategories[0]
                }
                // get students
                if (this.fastbridgeMonitor?.students) {
                    students = this.fastbridgeMonitor?.students.map(s => ({
                        studentId: s.student_id,
                        studentName: s.student_full_name,
                        goal: s.end_goal,
                        gradeId: s.assessment_grade_id,
                        originalGoal: s.end_goal,
                        originalGradeId: s.assessment_grade_id,
                        primary: true,
                        monitorIds: [],
                    }))
                }
            }

            this.monitors.push({
                fastbridgeMonitor: this.monitors.length === 0 && this.fastbridgeMonitor?.students,
                monitor_user_id: this.sessionUser.user.id,
                sub_category_id: subcategory,
                monitor_week_days: "",
                students,
                primary_flag,
                start_date: this.fastbridgeMonitor?.start_date || null,
                end_date: this.fastbridgeMonitor?.end_date || null,
                start_time: null,
                end_time: null,
                monitorIds: [],
                schedule: {},
            })

            this.changeTab(this.monitors.length - 1)
        },

        setEndTime(startTime, monitor, i) {
            if (!this.monitorLoaded[i]) {
                return
            }
            monitor.end_time = this.getEndTime(startTime, monitor.end_time)
        },

        measuredMonitorSelected(index) {
            return this.monitors[index]?.sub_category_id && this.monitors[index]?.sub_category_id?.id !== 'not_measured'
        },

        setPrimary(monitor, index) {
            if (monitor.primary_flag) {
                this.monitors.forEach((mon,i) => mon.primary_flag = i === index) // clear other indexes
            }
            this.refreshGoalGrid()
        },

        changeMeasureType(index) {
            const monitor = this.monitors[index]
            this.monitorBanner[index] = null
            this.monitorFormValid[index] = false
            monitor.students = [] // reload students
            this.loadMonitorDependencies(index)
        },

        async loadMonitorDependencies(index) {
            const monitor = this.monitors[index]

            const measureId = monitor.sub_category_id?.id
            if (!measureId) {
                return
            }
            if (measureId === "not_measured") {
                this.upperBanner = {
                    show: true,
                    text: 'When an intervention has no progress monitoring measure, scores and goals are not tracked within the intervention.',
                    usage: 'info',
                }
                this.monitors = this.monitors.filter((mon, i) => mon.sub_category_id.id === 'not_measured')
                this.activeTab = 0
                return
            }
            this.upperBanner.show = false
            this.monitorBanner[index] = null

            if (monitor.sub_category_id?.category_id) this.monitors[index].monitor_category_id = monitor.sub_category_id?.category_id

            if (this.students.length) {
                this.loading++
                await this.loadLinkableMonitors(monitor, index)
                this.loading--
            } else {
                this.monitorLoaded[index] = true
                this.resetValidation()
            }
        },

        async loadLinkableMonitors(monitor, index) {
            let queryParams = {
                student_id: this.students.map(r=>r.student_id).filter(id => id > 0),
                sub_category_id: monitor.sub_category_id?.id
            }

            // if the alpha map hasn't been loaded yet, load it
            let alphaMapPromise = Promise.resolve(true)
            if (!this.alphaMaps[queryParams.sub_category_id]) {
                alphaMapPromise = this.$axios.get(this.$models.getUrl('alphaMap', 'read') + '&' + this.$objectToParams({sub_category_id: queryParams.sub_category_id }))
                .then(response => {
                    if (!this.$ecResponse(response)) {
                        return Promise.reject("Goal mappings failed to load")
                    }
                    this.alphaMaps[queryParams.sub_category_id] = response.data.alpha_maps
                })
            }

            // load linkable monitors
            const linkableMonitorPromise = this.$axios.get(this.$models.getUrl('linkableStudentMonitors', 'read') + '&' + this.$objectToParams(queryParams))
            .then(response => {
                if (!this.$ecResponse(response)) {
                    return Promise.reject("Linkable monitors failed to load")
                }
                // use shared schedule if available
                if (response.data.sharedStartDate && !monitor.start_date) monitor.start_date = response.data.sharedStartDate
                if (response.data.sharedEndDate && !monitor.end_date) monitor.end_date = response.data.sharedEndDate
                if (response.data.sharedStartTime && !monitor.start_time) monitor.start_time = response.data.sharedStartTime
                if (response.data.sharedEndTime && !monitor.end_time) monitor.end_time = response.data.sharedEndTime
                if (response.data.sharedWeekDays && !monitor.monitor_week_days) monitor.monitor_week_days = response.data.sharedWeekDays
                if (response.data.sharedFrequencyCount && !monitor.monitor_frequency_cnt) monitor.monitor_frequency_cnt = response.data.sharedFrequencyCount

                // use intervention schedule if available
                if(Object.keys(this.schedule).length) {
                    if (!monitor.start_date) monitor.start_date = this.schedule.start_date
                    if (!monitor.end_date) monitor.end_date = this.schedule.end_date
                    if (!monitor.start_time) monitor.start_time = this.schedule.start_time
                    if (!monitor.end_time) monitor.end_time = this.schedule.end_time
                }

                // sort students by name
                let students = response.data.students
                students.sort((a,b) => a.studentName.localeCompare(b.studentName))

                // trim trailing zeros from goals
                students.forEach(student => {
                    student.monitors.forEach(m => {
                        if (m.goal && m.goal.includes('.')) {
                            m.goal = m.goal.replace(/(\.0+|(\.[0-9]*?)0+)$/, '$2')
                        }
                    })
                })

                // count goals and scores so we can display a banner if we have 1 or more goals and/or scores
                let goals = 0
                let scores = 0
                students.forEach(student => {
                    student.monitors.sort((a,b) => (b.primary - a.primary))
                    student.monitors.some(m => goals += m.goal ? 1 : 0)
                    student.monitors.some(m => scores += m.lastScore ? 1 : 0)
                })
                this.monitors[index].hasGoalsOrScores = goals || scores
                if (goals || scores) {
                    let numOfStudents = ''
                    if((goals > 0 && goals === students.length) && (scores > 0 && scores === students.length)){
                        numOfStudents = 'Students'
                    } else { // if not all students have goals and scores, return number of students
                        numOfStudents = Math.max(goals, scores) + ' student'
                        numOfStudents = numOfStudents > 1 ? numOfStudents + 's have' : numOfStudents + ' has'
                    }
                    let text = 'scores and/or goals'
                    this.monitorBanner[index] = {
                        show: true,
                        text: `${numOfStudents} ${text} that will link to this plan.`,
                        usage: 'success',
                    }
                }

                // version of ag-grid doesn't support rowSpan, so we will fake it visually
                // format students for grid - flatten the array of monitors into a single array of students
                students = students.flatMap(student => student.monitors.map((otherMonitor, i) => ({
                    studentId: student.studentId,
                    studentName: i == 0 ? student.studentName : '', // only show name on first row
                    rowSpan: student.monitors.length,
                    row: i + 1,
                    monitorIds: otherMonitor.monitorIds,
                    goal: otherMonitor.goal,
                    gradeId: otherMonitor.gradeId,
                    primary: monitor.primary_flag ? otherMonitor.primary : false,
                    lastScore: otherMonitor.lastScore,
                    originalGoal: otherMonitor.goal,
                    originalGradeId: otherMonitor.gradeId,
                })))

                // restore previous overrides
                if (this.value.length && this.value[index]?.students?.length) {
                    let previousOverrides = new Map();
                    this.value[index].students.forEach(s => {
                        previousOverrides.set(`${s.studentId}-` + s.monitorIds.join(','), {
                            goal: s.goal || '',
                            gradeId: s.gradeId || 0,
                            originalGoal: s.originalGoal || '',
                            originalGradeId: s.originalGradeId || 0,
                            primary: s.primary || false,
                        })
                    })
                    students.forEach(s => {
                        const key = `${s.studentId}-${s.monitorIds.join(',')}`
                        if (previousOverrides.has(key)) {
                            let override = previousOverrides.get(key)
                            if (override.goal !== '') s.goal = override.goal
                            if (override.gradeId !== 0) s.gradeId = override.gradeId
                            if (monitor.fastbridgeMonitor && override.originalGoal !== '') s.originalGoal = override.originalGoal
                            if (monitor.fastbridgeMonitor && override.originalGradeId !== 0) s.originalGradeId = override.originalGradeId
                            s.primary = override.primary
                        } else {
                            s.primary = false
                        }
                    })
                }

                this.monitors[index].students = students
                this.resetValidation()
            })

            return Promise.all([alphaMapPromise, linkableMonitorPromise])
            .then(() => {
                const someStudentHasMultipleGoals = this.monitors[index].students.some(s => s.rowSpan > 1)
                const alphaMapHash = new Map(this.alphaMaps?.[queryParams.sub_category_id]?.map(a => [decimalString(a.numeric_score, 11, 0), a.alpha_score]))
                let goal = {
                    headerName: 'Goal',
                    field: 'goal',
                    editable: v => v.data.originalGoal === '' ? true : false,
                    sortable: false,
                    cellStyle: v => {
                        return {
                            border: 'none',
                            'text-align': 'right',
                            color: v.data.originalGoal === '' ? '#006C90' : '#000000',
                            pointerEvents: v.data.originalGoal === '' ? 'auto' : 'none',
                        }
                    },
                    valueFormatter: v => {
                        if (alphaMapHash.size > 0 && alphaMapHash.has(decimalString(v.value, 9, 2))) {
                            return alphaMapHash.get(decimalString(v.value, 9, 2))
                        } else {
                            return v.value
                        }
                    },
                    cellRenderer: v => {
                        let html = ''
                        if (v.data.originalGoal === '' && !v.data.goal) {
                            if (this.showGoalErrors[index]) {
                                html += '<i class="fas fa-exclamation-circle" style="color: #BF0D00;margin-right: 5px;"></i>'
                            }
                            html += 'Add'
                        } else {
                            html += v.valueFormatted
                        }
                        return html
                    },
                    headerClass: "ag-right-aligned-header",
                    maxWidth: 110,
                }
                let level = {
                    headerName: this.monitors[index].hasGoalsOrScores ? 'Level' : 'Level (Optional)',
                    field: 'gradeId',
                    editable: v => !v.data.originalGradeId ? true : false,
                    sortable: false,
                    cellStyle: v => {
                        return {
                            border: 'none',
                            'text-align': 'right',
                            color: !v.data.originalGradeId ? '#006C90' : '#000000',
                            pointerEvents: !v.data.originalGradeId ? 'auto' : 'none'
                        }
                    },
                    onCellClicked: v => {
                        if (v.event.target.dataset.makePrimary) {
                            this.monitors[index].students.forEach(s => {
                                if (v.data.studentId === s.studentId) {
                                    if (s.row === v.data.row) {
                                        s.primary = true
                                    } else {
                                        s.primary = false
                                    }
                                }
                            })
                            this.refreshGoalGrid()
                        }
                    },
                    valueFormatter: v => {
                        return v.value ? this.grades.find(g => g.id == v.value)?.name : ''
                    },
                    cellRenderer: v => {
                        let html = ''
                        if (monitor.primary_flag && v.data.rowSpan > 1) {
                            html += '<div style="margin-top: 5px;color: #006C90; font-size: 14px;line-height:16px !important;">'
                        }
                        if (!v.data.originalGradeId && !v.data.gradeId) {
                            html += 'Add'
                        } else {
                            html += v.valueFormatted
                        }
                        if (monitor.primary_flag && v.data.rowSpan > 1) {
                            html += '</div>'
                            html += v.data.primary ? '<div style="color: #555555; font-size: 12px;line-height:14px !important;">Primary</div>' : '<div style="color: #006C90; font-size: 12px;line-height:20px !important;pointer-events: auto;"><span data-make-primary="true">Make Primary</span></div>'
                        }
                        return html
                    },
                    maxWidth: 140,
                    headerComponentFramework: 'iconHeader',
                    headerComponentParams: {
                        tooltipText: 'Levels (which could be grade levels) are optional, and if added, will appear on progress monitoring charts.',
                        iconClass: 'fal fa-info-circle'
                    },
                    cellEditorFramework: "FeGridChipSelect",
                    cellEditorParams: v => {
                        return {
                            value: v.value,
                            rowDataKey: 'assessment_grade_id',
                            mode: "edit",
                            multiple: false,
                            items: this.grades,
                            labelProp: 'name',
                            keyProp: 'id',
                            disableChips: true
                        }
                    },
                }
                if (this.alphaMaps?.[queryParams.sub_category_id]?.length > 0) {
                    goal.cellEditorFramework = 'FeGridChipSelect'
                    goal.cellEditorParams = v => {
                        return {
                            value: parseInt(v.value, 10),
                            rowDataKey: 'goal',
                            mode: 'edit',
                            multiple: false,
                            items: this.alphaMaps[queryParams.sub_category_id],
                            keyProp: 'numeric_score',
                            labelProp: 'alpha_score',
                            disableChips: true,
                        }
                    }
                }

                let lastScore = {
                    headerName: 'Last Score',
                    field: 'lastScore',
                    headerClass: "ag-right-aligned-header",
                    cellStyle: { border: 'none', 'text-align': 'right', pointerEvents: 'none' },
                    maxWidth: 130,
                    editable: false,
                    sortable: false,
                    valueFormatter: v => {
                        if (alphaMapHash.size > 0 && alphaMapHash.has(decimalString(v.value, 9, 2))) {
                            return alphaMapHash.get(decimalString(v.value, 9, 2))
                        } else {
                            return v.value || "\u2014"
                        }
                    },
                }
                let headers = [{
                    headerName: 'Student',
                    field: 'studentName',
                    sortable: false,
                    cellStyle: v => {
                        return {
                            border: 'none !important',
                            borderRight: someStudentHasMultipleGoals ? '1px solid #E0E0E0 !important' : 'none !important',
                            pointerEvents: 'none'
                        }
                    },
                    cellClassRules: { 'no-border-cell': true },
                    tooltipValueGetter: value => value.value,
                    valueFormatter: v => {
                        return this.topSecretModeActive ? v.data.studentId : v.data.studentName
                    }
                }]

                headers.push(level)
                if (this.monitors[index].students.some(m => m.lastScore !== '')) {
                    headers.push(lastScore)
                }
                headers.push(goal)

                this.$set(this.columnHeaders, queryParams.sub_category_id, headers)

                this.monitorLoaded[index] = true
                this.resetValidation()
            })
        },

        updateStudentGoalValue(monitorIndex, params) {
            let refreshGrid = false
            if (params.colDef.field === 'goal') {
                let newValue = decimalString(String(params.newValue), 8, 2)
                let oldValue = this.monitors[monitorIndex].students[params.rowIndex][params.colDef.field]
                if (oldValue !== newValue) {
                    this.monitors[monitorIndex].students[params.rowIndex][params.colDef.field] = newValue
                    refreshGrid = true
                }
            }
            if (this.submitted) {
                // stop showing goal errors if all students have a goal
                this.$refs.goalError[monitorIndex].validate()
                refreshGrid = true
            }
            if (refreshGrid) this.refreshGoalGrid()
        },

        getNumber(i) {
            const n = Math.abs(i + 1)
            const s = ["th", "st", "nd", "rd"]
            const v = n % 100
            return i + 1 + (s[(v - 20) % 10] || s[v] || s[0])
        },

        changeTab(monitorIndex) {
            this.activeTab = monitorIndex
            this.refreshGoalGrid()
        },

        removeMonitor(monitorIndex) {
            this.monitorFormValid.splice(monitorIndex, 1)
            this.monitorUserTouched.splice(monitorIndex, 1)
            this.monitorLoaded.splice(monitorIndex, 1)
            this.monitorBanner.splice(monitorIndex, 1)
            this.monitors.splice(monitorIndex, 1)
            this.changeTab(Math.max(0, monitorIndex - 1))
        },

        editField(field, monitorIndex) {
            this.$refs[field][monitorIndex]?.validate()
            this.$nextTick(() => {
                let refreshKey = ''
                if(field === 'startDatePicker') {
                    refreshKey = 'end_date'
                } else if (field === 'startTimePicker') {
                    refreshKey = 'end_time'
                }
                if (refreshKey) {
                    // clear the field and then set it back to the previous value to trigger a refresh because
                    // calling .validate() doesn't seem to trigger a refresh for these fields :shrug:
                    let prevVal = this.monitors[monitorIndex][refreshKey]
                    this.monitors[monitorIndex][refreshKey] = ''
                    this.$nextTick(() => this.monitors[monitorIndex][refreshKey] = prevVal)
                }
            })
        },
        nextStep() {
            this.submitted = true
            if (this.validateForms()) {
                this.$emit('go')
            } else {
                this.$emit('scrollToTop')
                this.refreshGoalGrid()
            }
        },
        refreshGoalGrid() {
            const grid = this.$refs.monitorGrid?.[this.activeTab]?.gridApi
            grid?.redrawRows() // needed to make sure the grid errors are reflected
            grid?.sizeColumnsToFit() // needed to make sure the grid is the correct width when switching tabs
        },
        resetValidation() {
            this.submitted = false
            this.monitors.forEach((mon, monitorIndex) => {
                const form = this.$refs.monitorConfig[monitorIndex]
                // A load finishing must not wipe the error on a field the user already edited.
                // FeRemoteCombo wraps the v-autocomplete that registers itself with the form.
                const edited = this.monitorUserTouched[monitorIndex]
                    ? this.$refs.monitorUser?.[monitorIndex]?.$refs.autocomplete
                    : null
                if (!edited || !Array.isArray(form.inputs)) {
                    form.resetValidation()
                    return
                }
                form.inputs.forEach(input => {
                    if (input !== edited) input.resetValidation()
                })
            })
        },
        validateForms() {
            return this.monitors.map((mon, monitorIndex) => this.$refs.monitorConfig[monitorIndex].validate()).every(m => m)
        },
        validateGoals(students) {
            if (students.some(s => s.originalGoal === '' && !s.goal)) {
                return 'Each student must have a goal.'
            }
            return true
        },
    }
}
</script>

<style lang="scss">
.interventions-add-measure-student-gradeId-divider {
    position: absolute;
    left: 0;
    width: 100vw;
    margin: 10px 0;
}
.interventions-add-measure-student-gradeId-div {
    line-height: 14px;
    color: #050F2D;
}
.interventions-add-measure-student-gradeId-primary {
    color: #495B8F;
    font-size: 12px;
}
.interventions-add-measure-student-gradeId-make-primary {
    color: #006C90;
    font-size: 12px;
}
</style>

<style lang="scss" scoped>
.monitor-tab {
    font-size: 14px;
    line-height: 14px;
    margin-right: 8px;
    cursor: pointer;
    color: #495B8F;
    padding: 10px 15px 15px 15px;

    &-active {
        color: #006C90;
        border-bottom: 3px solid #006C90;
    }
    &-error {
        color: #BF0D00 !important;
    }
    &:hover {
        background-color: #ECEDF1;
    }
}

.academic-monitor-schedule-student-goal-grid {
    ::v-deep .ag-header-cell {
        border-right: none !important;
    }
    ::v-deep .ag-header {
        height: 46px !important;
        min-height: 46px !important;
    }
    ::v-deep .ag-row .ag-cell {
        line-height: 46px !important;
    }
    ::v-deep .ag-header-row {
        font-weight: 500 !important;
    }
    ::v-deep .ag-root-wrapper-body.ag-layout-normal {
        height: 100% !important;
    }
}

.academic-monitoring-schedule-creation {
    ::v-deep .v-text-field__details {
        padding: 0 !important;
    }
    ::v-deep .error--text {
        color: #BF0D00 !important;
        font-size: 14px;
    }
    ::v-deep .v-text-field.error--text .v-input__slot {
        border-color: #BF0D00 !important;
    }
    ::v-deep .fe-label {
        margin-top: 0 !important;
    }

    ::v-deep .ag-row { border-bottom: none !important; }
    ::v-deep .ag-row .ag-cell { border-bottom: none !important; }
}
</style>
